import { Button, Dropdown, type MenuProps } from "antd";
import {
  DownloadOutlined,
  ProjectOutlined,
  BookOutlined,
  LogoutOutlined,
} from "@ant-design/icons";

import { useMst } from "../../stores/main";
import { observer } from "mobx-react-lite";
import "./BurgerMenu.scss";
import { BLINGS_API, getENV, HELP_CENTER_URL } from "../../stores/consts";

const MenuIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1.25 3.25C1.25 2.83579 1.58579 2.5 2 2.5H14C14.4142 2.5 14.75 2.83579 14.75 3.25C14.75 3.66421 14.4142 4 14 4H2C1.58579 4 1.25 3.66421 1.25 3.25ZM1.25 8C1.25 7.58579 1.58579 7.25 2 7.25H14C14.4142 7.25 14.75 7.58579 14.75 8C14.75 8.41421 14.4142 8.75 14 8.75H2C1.58579 8.75 1.25 8.41421 1.25 8ZM2 12C1.58579 12 1.25 12.3358 1.25 12.75C1.25 13.1642 1.58579 13.5 2 13.5H14C14.4142 13.5 14.75 13.1642 14.75 12.75C14.75 12.3358 14.4142 12 14 12H2Z"
      fill="currentColor"
    />
  </svg>
);

const IntegrationIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.5303 1.53033C15.8232 1.23744 15.8232 0.762563 15.5303 0.46967C15.2374 0.176777 14.7626 0.176777 14.4697 0.46967L13.1801 1.75929C12.4932 1.28066 11.658 0.999998 10.7574 0.999998C9.63214 0.999998 8.55301 1.44699 7.75736 2.24264L7.17707 2.82293C6.80313 3.19687 6.80313 3.80313 7.17707 4.17707L11.8229 8.82293C12.1969 9.19687 12.8031 9.19687 13.1771 8.82293L13.7574 8.24264C14.553 7.44699 15 6.36786 15 5.24264C15 4.34196 14.7193 3.50684 14.2407 2.81995L15.5303 1.53033ZM7.03033 6.46967C7.32322 6.76256 7.32322 7.23744 7.03033 7.53033L5.78033 8.78033L7.21968 10.2197L8.46968 8.96968C8.76257 8.67679 9.23745 8.67679 9.53034 8.96968C9.82323 9.26257 9.82323 9.73745 9.53034 10.0303L8.28034 11.2803L8.82293 11.8229C9.19687 12.1969 9.19687 12.8031 8.82293 13.1771L8.24264 13.7574C7.44699 14.553 6.36786 15 5.24264 15C4.34196 15 3.50684 14.7193 2.81995 14.2407L1.53033 15.5303C1.23744 15.8232 0.762563 15.8232 0.46967 15.5303C0.176777 15.2374 0.176777 14.7626 0.46967 14.4697L1.75929 13.1801C1.28066 12.4932 1 11.658 1 10.7574C1 9.63214 1.44699 8.55301 2.24264 7.75736L2.82293 7.17707C3.19687 6.80313 3.80313 6.80313 4.17707 7.17707L4.71967 7.71967L5.96967 6.46967C6.26256 6.17678 6.73744 6.17678 7.03033 6.46967ZM3.5 8.62132L3.3033 8.81802C2.78896 9.33236 2.5 10.03 2.5 10.7574C2.5 12.2721 3.72792 13.5 5.24264 13.5C5.97004 13.5 6.66763 13.211 7.18198 12.6967L7.37868 12.5L3.5 8.62132ZM12.6967 7.18198L12.5 7.37868L8.62132 3.5L8.81802 3.3033C9.33237 2.78895 10.03 2.5 10.7574 2.5C12.2721 2.5 13.5 3.72792 13.5 5.24264C13.5 5.97003 13.211 6.66763 12.6967 7.18198Z"
      fill="currentColor"
    />
  </svg>
);

const demoDownloadUrl =
  "https://assets.blings.io/demo/OnBoardingVideoCollect.zip";
const extensionDownloadUrl = `${
  BLINGS_API[getENV()]
}/extension/latest/download`;

export const BurgerMenu = observer(() => {
  const {
    platformStore: { logout, selectedProjectId },
  } = useMst();

  const handleDownload = (url: string) => {
    const link = document.createElement("a");
    link.download = "demo";
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    link.remove();
  };

  const PLATFORM = `https://${
    getENV() === "dev" ? "dev." : ""
  }platform.blings.io/`;

  const menuItems: MenuProps["items"] = [
    {
      key: "platform",
      icon: <ProjectOutlined />,
      label: "Platform",
      onClick: () =>
        window.open(
          selectedProjectId
            ? PLATFORM + "project/" + selectedProjectId
            : PLATFORM,
          "_blank"
        ),
    },
    {
      key: "integration",
      icon: (
        <span className="burger-menu-svg-icon">
          <IntegrationIcon />
        </span>
      ),
      label: "Integration",
      onClick: () =>
        window.open(
          selectedProjectId
            ? PLATFORM + "project/" + selectedProjectId + "/integration"
            : PLATFORM + "integration",
          "_blank"
        ),
    },
    { type: "divider" },
    {
      key: "demo-files",
      icon: <DownloadOutlined />,
      label: "Demo Files",
      onClick: () => handleDownload(demoDownloadUrl),
    },
    {
      key: "extension",
      icon: <DownloadOutlined />,
      label: "Extension",
      onClick: () => handleDownload(extensionDownloadUrl),
    },
    {
      key: "knowledge-base",
      icon: <BookOutlined />,
      label: "Knowledge Base",
      onClick: () => window.open(HELP_CENTER_URL, "_blank"),
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Log out",
      onClick: () => logout(),
    },
  ];

  return (
    <div id="avatar-menu">
      <Dropdown
        menu={{ items: menuItems }}
        placement="bottomRight"
        trigger={["click"]}
        overlayClassName="avatar-menu-dropdown"
      >
        <Button
          type="text"
          className="menu-button"
          icon={<MenuIcon />}
        />
      </Dropdown>
    </div>
  );
});
